import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Download, Monitor, Smartphone, Tablet, Loader2, Trash2, ShieldCheck, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * GdprAndDevicesSection
 *
 * Mitglieder-Selbstbedienung:
 *  1. DSGVO Art. 15 Datenauskunft — komplettes Datenarchiv als ZIP herunterladen
 *  2. Aktive vertraute Geräte ("Remember this device") sehen + einzeln/komplett widerrufen
 *
 * Beides ist Pflicht/Best-Practice für eine DSGVO-konforme und sicherheitstransparente
 * Anwendung. Erweitert ContactDetailsPage ohne die bestehende Account-Lösch-UI zu duplizieren.
 */
export function GdprAndDevicesSection() {
    const [downloading, setDownloading] = useState(false);
    const [devices, setDevices] = useState([]);
    const [ttlDays, setTtlDays] = useState(30);
    const [loading, setLoading] = useState(true);
    const [revokingId, setRevokingId] = useState(null);
    const [revokingAll, setRevokingAll] = useState(false);

    const loadDevices = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/auth/2fa/trusted-devices');
            setDevices(data.devices || []);
            setTtlDays(data.ttl_days || 30);
        } catch (e) {
            // Endpoint funktioniert nur bei aktivem 2FA - nicht kritisch
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadDevices(); }, []);

    const handleExport = async () => {
        setDownloading(true);
        toast.info('Datenauskunft wird zusammengestellt …');
        try {
            const res = await api.get('/portal/my-data/export', { responseType: 'blob' });
            // Filename aus Content-Disposition extrahieren
            const cd = res.headers['content-disposition'] || '';
            const match = cd.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : `bgpr_datenauskunft_${new Date().toISOString().slice(0, 10)}.zip`;
            const blob = new Blob([res.data], { type: 'application/zip' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success('Datenauskunft heruntergeladen');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Datenauskunft fehlgeschlagen');
        } finally {
            setDownloading(false);
        }
    };

    const handleRevoke = async (deviceId) => {
        if (!window.confirm('Diesen Geräte-Zugang wirklich widerrufen? Bei der nächsten Anmeldung wird auf diesem Gerät erneut der 2FA-Code abgefragt.')) return;
        setRevokingId(deviceId);
        try {
            await api.post('/auth/2fa/revoke-device', { device_id: deviceId });
            toast.success('Gerät widerrufen');
            await loadDevices();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Widerruf fehlgeschlagen');
        } finally {
            setRevokingId(null);
        }
    };

    const handleRevokeAll = async () => {
        if (!window.confirm(`Wirklich ALLE ${devices.length} vertrauten Geräte widerrufen? Bei künftigen Anmeldungen wird auf jedem Gerät erneut der 2FA-Code abgefragt — auch auf diesem hier.`)) return;
        setRevokingAll(true);
        try {
            await api.post('/auth/2fa/revoke-all-devices');
            toast.success('Alle Geräte widerrufen');
            await loadDevices();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Widerruf fehlgeschlagen');
        } finally {
            setRevokingAll(false);
        }
    };

    const fmt = (iso) => {
        if (!iso) return '—';
        try { return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }); }
        catch { return iso; }
    };

    const deviceIcon = (ua = '') => {
        const lower = ua.toLowerCase();
        if (/iphone|android|mobile/.test(lower)) return <Smartphone className="h-4 w-4" />;
        if (/ipad|tablet/.test(lower)) return <Tablet className="h-4 w-4" />;
        return <Monitor className="h-4 w-4" />;
    };
    const formatUa = (ua = '') => {
        const lower = ua.toLowerCase();
        let browser = 'Browser';
        for (const [n, l] of [['edg/', 'Edge'], ['chrome/', 'Chrome'], ['firefox/', 'Firefox'], ['safari/', 'Safari']]) {
            if (lower.includes(n)) { browser = l; break; }
        }
        let os = 'Gerät';
        for (const [n, l] of [['iphone', 'iPhone'], ['android', 'Android'], ['windows', 'Windows'], ['mac os x', 'macOS'], ['linux', 'Linux']]) {
            if (lower.includes(n)) { os = l; break; }
        }
        return `${browser} auf ${os}`;
    };

    return (
        <>
            {/* DSGVO Art. 15 — Datenauskunft */}
            <Card className="mt-6 border-sky-200 bg-sky-50/30" data-testid="gdpr-export-card">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2 text-sky-900">
                        <FileDown className="h-4 w-4" /> Meine Daten herunterladen (DSGVO Art. 15)
                    </CardTitle>
                    <CardDescription className="text-sky-900/80">
                        Sie haben das Recht auf eine vollständige Auskunft über alle bei uns gespeicherten
                        personenbezogenen Daten. Mit einem Klick erhalten Sie ein ZIP-Archiv mit Profil,
                        Buchungen, Lizenzen, Fortbildungsnachweisen, Rechnungen, Sicherheits-Logs und
                        versendeten System-E-Mails — strukturiert und maschinenlesbar (JSON).
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Button
                        onClick={handleExport}
                        disabled={downloading}
                        className="bg-sky-600 hover:bg-sky-700"
                        data-testid="gdpr-export-btn"
                    >
                        {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                        Datenauskunft herunterladen
                    </Button>
                    <p className="text-xs text-sky-900/70 mt-3">
                        Die Datei enthält ein <code className="bg-white/70 px-1 rounded">README.txt</code> mit
                        Erläuterungen zu jedem Datensatz. Sensible Sicherungsmaßnahmen (Passwort-Hash,
                        2FA-Secret) sind <strong>nicht</strong> enthalten, da sie keine personenbezogenen
                        Daten im Sinne von Art. 15 darstellen.
                    </p>
                </CardContent>
            </Card>

            {/* Meine aktiven Geräte */}
            <Card className="mt-6 border-emerald-200 bg-emerald-50/30" data-testid="trusted-devices-card">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2 text-emerald-900">
                        <ShieldCheck className="h-4 w-4" /> Meine vertrauten Geräte
                    </CardTitle>
                    <CardDescription className="text-emerald-900/80">
                        Auf diesen Geräten haben Sie „Dieses Gerät merken" beim 2FA-Login aktiviert.
                        Sie überspringen dort die Code-Eingabe für {ttlDays} Tage. Bei Verdacht auf
                        Verlust oder Diebstahl bitte einzeln widerrufen.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" /> Lade Geräteliste …
                        </div>
                    ) : devices.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            Keine aktiven vertrauten Geräte. Bei jeder Anmeldung wird ein 2FA-Code abgefragt
                            (sofern 2FA für dieses Konto aktiv ist).
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {devices.map(d => (
                                <div
                                    key={d.id}
                                    className="flex items-center justify-between gap-3 p-3 bg-white rounded-lg border border-emerald-200"
                                    data-testid={`trusted-device-${d.id}`}
                                >
                                    <div className="flex items-start gap-3 min-w-0 flex-1">
                                        <div className="mt-0.5 text-emerald-700">{deviceIcon(d.user_agent)}</div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-gray-900 truncate">
                                                {d.label || formatUa(d.user_agent)}
                                            </p>
                                            <p className="text-xs text-muted-foreground truncate" title={d.user_agent}>
                                                {formatUa(d.user_agent)}
                                            </p>
                                            <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground flex-wrap">
                                                <span>Erstellt: {fmt(d.created_at)}</span>
                                                {d.last_used_at && <span>· Zuletzt verwendet: {fmt(d.last_used_at)}</span>}
                                                <Badge variant="outline" className="text-[10px] py-0">gültig bis {fmt(d.expires_at)}</Badge>
                                            </div>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRevoke(d.id)}
                                        disabled={revokingId === d.id}
                                        className="text-rose-700 hover:bg-rose-50"
                                        data-testid={`revoke-device-${d.id}`}
                                    >
                                        {revokingId === d.id
                                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            : <Trash2 className="h-3.5 w-3.5" />}
                                    </Button>
                                </div>
                            ))}
                            {devices.length > 1 && (
                                <Button
                                    variant="outline"
                                    onClick={handleRevokeAll}
                                    disabled={revokingAll}
                                    className="border-rose-300 text-rose-700 hover:bg-rose-50 w-full"
                                    data-testid="revoke-all-devices-btn"
                                >
                                    {revokingAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                                    Alle Geräte widerrufen
                                </Button>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>
        </>
    );
}
